import React, {createContext, ForwardedRef, forwardRef, useContext} from "react";
import * as ReactFinalForm from 'react-final-form';
import YupExtensions, {Yup} from "./yup-extensions";

declare module "react" {
    function forwardRef<T, P = {}>(
        render: (props: P, ref: React.Ref<T>) => React.ReactNode | null
    ): (props: P & React.RefAttributes<T>) => React.ReactNode | null;
}

interface FormProps<FormValues = Record<string, any>, InitialFormValues = Partial<FormValues>> extends Omit<ReactFinalForm.FormProps<FormValues, InitialFormValues>, 'render' | 'component'> {
    children: ReactFinalForm.FormProps<FormValues, InitialFormValues>['children'];
    formProps?: Partial<Omit<React.DetailedHTMLProps<React.FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>, 'onSubmit' | 'children' | 'ref'>>,
}

YupExtensions.injectExtensions();


const FormInner = <FormValues = Record<string, any>, InitialFormValues = Partial<FormValues>>(
    props: FormProps<FormValues, InitialFormValues>,
    ref: ForwardedRef<HTMLFormElement>
) => {

    return (
        <ReactFinalForm.Form<FormValues, InitialFormValues>
            {...props}
            onSubmit={props.onSubmit}
        >
            {(renderProps) => {
                return (
                    <FormContext.Provider value={renderProps as FormContextType}>
                        <form
                            noValidate
                            {...props.formProps}
                            onSubmit={renderProps.handleSubmit}
                            ref={ref}
                        >
                            {
                                typeof props.children !== 'function'
                                    ? props.children
                                    // @ts-ignore
                                    : props.children(renderProps)
                            }
                        </form>
                    </FormContext.Provider>
                );
            }}
        </ReactFinalForm.Form>
    );
}

const Form = forwardRef(FormInner);


type FormContextType<FormValues = Record<string, any>, InitialFormValues = Partial<FormValues>> = ReactFinalForm.FormRenderProps<FormValues, InitialFormValues>;


/**
 * The context for providing the form inside the form-related components.
 */
const FormContext = createContext<FormContextType | null>(null);

/**
 * A hook that exposes the FormContext's value to the using component.
 */
export const useForm = <FormValues = Record<string, any>, InitialFormValues = Partial<FormValues>>() => {
    const context = useContext(FormContext);
    return context as FormContextType<FormValues, InitialFormValues> | null;
}


export {Yup};
export * as MuiRFF from 'mui-rff';
export * as ReactFinalForm from 'react-final-form';
export * as FinalForm from 'final-form';
export default Form;
